/**
 * The same letter as plain text.
 *
 * Not a nicety. `readiness.ts` treats an empty `bodyText` as a problem worth
 * warning about, and it is right to: a multipart message without a text part is
 * a spam signal on its own, and the clients that show text only are exactly the
 * conservative corporate ones this campaign is aimed at.
 *
 * The rule followed here is that the text version must say the same things, not
 * look the same. Links come out as "label <url>" rather than disappearing,
 * because a text reader who cannot see the button still has to be able to act,
 * and the unsubscribe line is written out in full for the same reason.
 */
import { UNSUB_TOKEN, safeUrl } from "./render";
import { resolveProps, type Block, type ColumnSlot, type EmailDesign } from "./types";

/** HTML back to something a person reads. Block-level tags become breaks, the
 *  rest goes; entities that the editor produces are decoded by hand, since
 *  there is no DOM in the tests. */
export function htmlToText(html: unknown): string {
  return String(html ?? "")
    .replace(/<script[\s\S]*?<\/script\s*>/gi, "")
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/\s*(p|div|h[1-6]|tr|li)\s*>/gi, "\n")
    .replace(/<\s*li[^>]*>/gi, "- ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function blockToText(block: Block): string {
  const p = resolveProps(block);

  switch (block.type) {
    case "heading":
      return String(p.text ?? "").trim();

    case "text":
    case "html":
      return htmlToText(p.html);

    case "button": {
      const url = safeUrl(p.url);
      const label = String(p.text ?? "").trim();
      return url ? `${label}: ${url}` : label;
    }

    case "image": {
      // An image with no alt text has nothing to say to a text reader, and a
      // bare "[image]" line is noise. A link behind it does have something.
      const alt = String(p.alt ?? "").trim();
      const href = safeUrl(p.href);
      if (href && alt) return `${alt}: ${href}`;
      return alt;
    }

    case "divider":
      return "---";

    case "spacer":
      return "";

    case "columns":
      return (((p.cols as ColumnSlot[] | undefined) ?? [])
        .flatMap((c) => c.children ?? [])
        .map(blockToText)
        .filter(Boolean)
        .join("\n\n"));

    case "quote": {
      const author = String(p.author ?? "").trim();
      const body = `"${String(p.text ?? "").trim()}"`;
      return author ? `${body}\n- ${author}` : body;
    }

    case "list":
      return String(p.items ?? "")
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean)
        .map((l) => `- ${l}`)
        .join("\n");

    case "social":
      return ((p.links as { label: string; url: string }[] | undefined) ?? [])
        .filter((l) => String(l.url ?? "").trim())
        .map((l) => `${l.label}: ${safeUrl(l.url)}`)
        .join("\n");

    case "signature":
      return [p.name, p.title, p.email, p.phone]
        .map((v) => String(v ?? "").trim())
        .filter(Boolean)
        .join("\n");

    case "footer":
      return [
        String(p.text ?? "").trim(),
        String(p.address ?? "").trim(),
        `${String(p.unsubscribeText ?? "").trim()}: ${UNSUB_TOKEN}`,
      ]
        .filter(Boolean)
        .join("\n");

    default:
      return "";
  }
}

export function renderEmailText(design: EmailDesign): string {
  return `${design.blocks.map(blockToText).filter((s) => s.trim()).join("\n\n")}\n`;
}
